import React from 'react'
import { useDispatch } from 'react-redux';
import { selectAddFilesDocument, setDraggingDocuments, setIsUpdateDocuments } from '../../../../../../features/actuarial/document-slice';
import UploadArea from '../../../../../ui/upload-area';
import DocumentList from '../../../../../ui/document-list';
import { CloseIconSVG, EditIconSVG, SaveIconSVG } from '../../../../../../assets/icon/svg';
import LoadingGlobal from '../../../../../ui/loading-global';
import { useComputationActions } from '../../../../../../hooks/actions/user-computation-actions';
import { useProposalComputation } from '../../../../../../hooks/proposal/use-proposal-computation';

export default function Documents() {

    const dispatch = useDispatch();
    const {
        documents,
        isDraggingDocuments,
        isUpdateDocuments,
    } = useProposalComputation()
    const { 
        cantEdit, 
        handleSaveDocuments, 
        handleCancelSaveDocuments, 
        isLoadingUploadDocuments 
    } = useComputationActions();


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        dispatch(selectAddFilesDocument(selectedFiles));
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        if(!isUpdateDocuments) return
        e.preventDefault();
        e.stopPropagation();
        dispatch(setDraggingDocuments(false));

        const items = Array.from(e.dataTransfer.items || []);
        const validFiles: File[] = [];

        items.forEach((item) => {
            if (item.kind !== "file") return;

            const entry = (item as any).webkitGetAsEntry?.();
            if (entry?.isDirectory) return;

            const file = item.getAsFile();
            if (file) validFiles.push(file);
        });

        if (validFiles.length > 0) {
            dispatch(selectAddFilesDocument(validFiles));
        }
    };
    
    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden ">
            <div className='flex-1 min-h-0 overflow-y-auto py-2 mb-1'>
                <div className='rounded-lg border border-gray-200 bg-white shadow-sm'>
                    <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="h-2 w-2 shrink-0 rounded-full bg-[#186cb2]" />
                                <h3 className="truncate text-xs font-semibold text-gray-800">
                                    Documents
                                </h3>
                            </div>
                            <p className="mt-0.5 text-[10px] leading-4 text-gray-500 sm:text-[11px]">
                                Attach all supporting documents.
                            </p>
                        </div>
                        {!isUpdateDocuments ? (
                            <button
                                disabled={cantEdit}
                                onClick={() => dispatch(setIsUpdateDocuments(true))}
                                className="flex size-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-[#186cb2] transition-colors hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                            >
                                <EditIconSVG size={14} />
                                <span className="hidden sm:inline text-xs">Edit Document</span>
                            </button>
                        ) : (
                            <div className="flex shrink-0 items-center gap-2">
                                <button
                                    disabled={cantEdit}
                                    onClick={handleCancelSaveDocuments}
                                    className="flex size-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                                >
                                    <CloseIconSVG size={14} />
                                    <span className="hidden sm:inline text-xs">Cancel</span>
                                </button>
                                <button
                                    disabled={documents.length === 0 || cantEdit}
                                    onClick={handleSaveDocuments}
                                    className="flex size-9 items-center justify-center rounded-md border border-[#186cb2] bg-[#186cb2] text-white transition-colors hover:bg-[#155b96] disabled:cursor-not-allowed disabled:opacity-50 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5"
                                >
                                    <SaveIconSVG size={14} />
                                    <span className="hidden sm:inline text-xs">Save</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="p-3 space-y-2">
                        <UploadArea
                            isDisabled={isUpdateDocuments}
                            length={documents.length}
                            isDragging={isDraggingDocuments}
                            onDragOver={(e) => {
                                if(!isUpdateDocuments) return
                                e.preventDefault();
                                dispatch(setDraggingDocuments(true));
                            }}
                            onDragLeave={(e) => {
                                if(!isUpdateDocuments) return
                                e.preventDefault();
                                dispatch(setDraggingDocuments(false));
                            }}
                            onDrop={handleDrop}
                            onFileChange={handleFileChange}
                        />
                        <DocumentList isDisabled={isUpdateDocuments}/> 
                    </div>
                </div>
            </div>
            {isLoadingUploadDocuments && (<LoadingGlobal />)}
        </div>
    )
}
